/**
 * Bay mode (X7) -- EXPERT_PORTAL_UX_PLAN.md §4 + §6.3.
 *
 * The other half of the two-mode split from Bench mode: a real golfer in the
 * chair, launch monitor running. "Baseline -> test builds -> record what each
 * one did." Every shot captured here lands in `launch_monitor_shots` with the
 * build (head_id/shaft_id/shaft_flex) attached and `is_baseline` set -- the
 * linkage that did not exist before X7, and the route out of the V9 physics
 * pause (§9.7): real shot data, not more estimates.
 *
 * Reuses LaunchMonitorImportReview (the same OCR/CSV/manual capture modal
 * every other launch-monitor entry point in the app uses) via its new
 * `buildContext` prop, rather than duplicating capture UI. "Which build" is
 * either the golfer's current club (baseline, no catalog attachment required)
 * or one of the builds this expert already composed for this profile in the
 * Fitter's Corner's Pass 04 (`GET /expert/builds`) -- bay mode tests candidates
 * the bench already shortlisted, it does not reopen the whole catalog.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import GolfLoadingSpinner from '../components/GolfLoadingSpinner';
import LaunchMonitorImportReview, { BuildContext } from '../components/LaunchMonitorImportReview';
import {
  getFittingShots,
  newFittingId,
  FittingShotsResponse,
} from '../services/launchMonitorShotService';
import './FittersCornerPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const category = 'driver'; // same scope note as the bench: driver only for now

interface BuildScoreRow {
  id: number;
  head_id: number | null;
  shaft_id: number | null;
  shaft_flex: string | null;
  score: number;
  source: 'matrix' | 'ideal' | 'probe';
}

interface CatalogItem { id: number; brand: string; model: string; standard_flex?: string | null; }

// A composed build can appear more than once in expert_build_scores (matrix vs.
// ideal rows for the same head+shaft) -- collapse to one testable candidate per
// (head_id, shaft_id, shaft_flex), highest score first.
function dedupeBuilds(rows: BuildScoreRow[]): BuildScoreRow[] {
  const byKey = new Map<string, BuildScoreRow>();
  for (const r of rows) {
    if (r.head_id == null || r.shaft_id == null) continue;
    const key = `${r.head_id}:${r.shaft_id}:${r.shaft_flex ?? ''}`;
    const existing = byKey.get(key);
    if (!existing || r.score > existing.score) byKey.set(key, r);
  }
  return Array.from(byKey.values()).sort((a, b) => b.score - a.score);
}

const fmt = (v: number | null | undefined, digits = 1): string => (v == null ? '—' : v.toFixed(digits));

const BayModePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const profileIdParam = searchParams.get('profile_id');

  const [profile, setProfile] = useState<any | null>(null);
  const [heads, setHeads] = useState<CatalogItem[]>([]);
  const [shafts, setShafts] = useState<CatalogItem[]>([]);
  const [builds, setBuilds] = useState<BuildScoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fittingId] = useState<string>(() => newFittingId());
  const [summary, setSummary] = useState<FittingShotsResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [capture, setCapture] = useState<BuildContext | null>(null);
  const [selectedBuildKey, setSelectedBuildKey] = useState<string>('');
  const [manualHeadId, setManualHeadId] = useState<number | null>(null);
  const [manualShaftId, setManualShaftId] = useState<number | null>(null);

  const authHeaders = useCallback(() => {
    const storageType = localStorage.getItem('auth_storage_type');
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    const token = storage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!profileIdParam) {
      setError('Bay mode needs a profile — open it from a golfer in the Fitter\'s Corner.');
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = authHeaders();
        const [profileRes, catalogRes, buildsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/v1/expert/profiles/${profileIdParam}`, { headers }),
          axios.get(`${API_BASE_URL}/api/v1/expert/rules/catalog?category=${category}`, { headers }),
          axios.get(`${API_BASE_URL}/api/v1/expert/builds?profile_id=${profileIdParam}&category=${category}`, { headers }),
        ]);
        setProfile(profileRes.data.profile);
        setShafts(catalogRes.data.shafts);
        setHeads(catalogRes.data.heads);
        setBuilds(buildsRes.data.builds);
      } catch (err) {
        console.error('Error loading bay mode:', err);
        setError(
          axios.isAxiosError(err) && err.response
            ? `The server returned ${err.response.status} loading this golfer.`
            : 'Could not reach the server.'
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [profileIdParam, authHeaders]);

  const candidates = useMemo(() => dedupeBuilds(builds), [builds]);
  const headLabel = (id: number | null) => { const h = heads.find(x => x.id === id); return h ? `${h.brand} ${h.model}` : `head #${id}`; };
  const shaftLabel = (id: number | null) => { const s = shafts.find(x => x.id === id); return s ? `${s.brand} ${s.model}` : `shaft #${id}`; };

  const refreshSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await getFittingShots(fittingId);
      setSummary(data);
    } catch (err) {
      // 404 just means nothing captured yet this session -- not an error state.
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [fittingId]);

  const startBaselineCapture = () => {
    if (!profile) return;
    setCapture({
      profileId: profile.id,
      headId: manualHeadId,
      shaftId: manualShaftId,
      shaftFlex: manualShaftId ? (shafts.find(s => s.id === manualShaftId)?.standard_flex ?? null) : null,
      isBaseline: true,
      fittingId,
      buildLabel: manualHeadId
        ? `Baseline — ${headLabel(manualHeadId)}${manualShaftId ? ` / ${shaftLabel(manualShaftId)}` : ''}`
        : 'Baseline — current build',
    });
  };

  const startBuildCapture = () => {
    if (!profile || !selectedBuildKey) return;
    const build = candidates.find(b => `${b.head_id}:${b.shaft_id}:${b.shaft_flex ?? ''}` === selectedBuildKey);
    if (!build) return;
    setCapture({
      profileId: profile.id,
      headId: build.head_id,
      shaftId: build.shaft_id,
      shaftFlex: build.shaft_flex,
      isBaseline: false,
      fittingId,
      buildLabel: `${headLabel(build.head_id)} / ${shaftLabel(build.shaft_id)}${build.shaft_flex ? ` (${build.shaft_flex})` : ''}`,
    });
  };

  const onCaptureConfirmed = () => {
    setCapture(null);
    refreshSummary();
  };

  if (loading) {
    return (
      <div className="bench">
        <div className="bench-loading"><GolfLoadingSpinner message="Loading the bay..." size="large" /></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="bench">
        <div className="bench-error">
          <p>{error || 'No profile available.'}</p>
          <button className="bench-btn pri" onClick={() => navigate('/expert')} style={{ marginTop: 12 }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bench">
      <div className="bench-topbar">
        <div className="bench-brand">Bay <em>Mode</em></div>
        <button
          className="bench-back"
          onClick={() => navigate(`/expert/bench?profile_id=${profile.id}&pass=build`)}
        >
          &larr; Back to bench
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-2)' }}>{user?.email}</div>
        <button className="bench-back" onClick={logout}>Log out</button>
      </div>

      <div className="bench-shell">
        <main className="bench-main">
          <div className="bench-head">
            <h1>Profile #{profile.id} — real customer, launch monitor running</h1>
            <p>
              Baseline first, then each test build. Every shot lands in <code>launch_monitor_shots</code> with
              the build attached — the piece the V9 physics rebuild is paused waiting on.
            </p>
          </div>

          <div className="bench-card">
            <h3>1. Baseline — his current build</h3>
            <p className="hint">
              If you know the head/shaft he's currently playing, attach it. If you don't, capture
              the shots anyway — an unattached baseline is still real, just not tied to a specific
              catalog row (§0.9: current-driver shaft model is populated on only 19% of real profiles).
            </p>
            <div className="bench-idealrow">
              <select className="bench-idealsel" value={manualHeadId ?? ''} onChange={e => setManualHeadId(e.target.value ? +e.target.value : null)}>
                <option value="">current head (optional)&hellip;</option>
                {heads.map(h => <option key={h.id} value={h.id}>{h.brand} {h.model}</option>)}
              </select>
              <select className="bench-idealsel" value={manualShaftId ?? ''} onChange={e => setManualShaftId(e.target.value ? +e.target.value : null)}>
                <option value="">current shaft (optional)&hellip;</option>
                {shafts.map(s => <option key={s.id} value={s.id}>{s.brand} {s.model} {s.standard_flex || ''}</option>)}
              </select>
              <button className="bench-btn pri" onClick={startBaselineCapture}>Capture baseline shot &rarr;</button>
            </div>
          </div>

          <div className="bench-card">
            <h3>2. Test builds</h3>
            {candidates.length === 0 ? (
              <p className="hint">
                Nothing composed for this golfer yet — go back to the bench's Pass 04 and build a
                shortlist first, then come test it here.
              </p>
            ) : (
              <>
                <p className="hint">Pick one of the builds you shortlisted at the bench, then capture a shot for it.</p>
                <div className="bench-idealrow">
                  <select
                    className="bench-idealsel"
                    value={selectedBuildKey}
                    onChange={e => setSelectedBuildKey(e.target.value)}
                  >
                    <option value="">choose a build&hellip;</option>
                    {candidates.map(b => {
                      const key = `${b.head_id}:${b.shaft_id}:${b.shaft_flex ?? ''}`;
                      return (
                        <option key={key} value={key}>
                          {headLabel(b.head_id)} / {shaftLabel(b.shaft_id)}{b.shaft_flex ? ` (${b.shaft_flex})` : ''} — scored {b.score}
                        </option>
                      );
                    })}
                  </select>
                  <button className="bench-btn pri" disabled={!selectedBuildKey} onClick={startBuildCapture}>
                    Capture shot for this build &rarr;
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="bench-card">
            <h3>3. What each one did</h3>
            {summaryLoading ? (
              <span className="bench-empty">Loading&hellip;</span>
            ) : !summary || (!summary.baseline && summary.builds.length === 0) ? (
              <span className="bench-empty">Nothing captured yet this fitting.</span>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bench-fitting-table">
                  <thead>
                    <tr>
                      <th>Build</th>
                      <th>Shots</th>
                      <th>Ball speed</th>
                      <th>Club speed</th>
                      <th>Smash</th>
                      <th>Launch</th>
                      <th>Spin</th>
                      <th>Carry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.baseline && (
                      <tr>
                        <td><strong>Baseline</strong></td>
                        <td>{summary.baseline.n_shots}</td>
                        <td>{fmt(summary.baseline.ball_speed_mph)}</td>
                        <td>{fmt(summary.baseline.club_speed_mph)}</td>
                        <td>{fmt(summary.baseline.smash_factor, 2)}</td>
                        <td>{fmt(summary.baseline.launch_angle_deg)}</td>
                        <td>{fmt(summary.baseline.spin_rate_rpm, 0)}</td>
                        <td>{fmt(summary.baseline.carry_yds)}</td>
                      </tr>
                    )}
                    {summary.builds.map(b => (
                      <tr key={`${b.head_id}-${b.shaft_id}-${b.shaft_flex}`}>
                        <td>{headLabel(b.head_id)} / {shaftLabel(b.shaft_id)}{b.shaft_flex ? ` (${b.shaft_flex})` : ''}</td>
                        <td>{b.n_shots}</td>
                        <td>{fmt(b.ball_speed_mph)}</td>
                        <td>{fmt(b.club_speed_mph)}</td>
                        <td>{fmt(b.smash_factor, 2)}</td>
                        <td>{fmt(b.launch_angle_deg)}</td>
                        <td>{fmt(b.spin_rate_rpm, 0)}</td>
                        <td>{fmt(b.carry_yds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {capture && (
        <LaunchMonitorImportReview
          clubType="driver"
          buildContext={capture}
          onConfirm={onCaptureConfirmed}
          onClose={() => setCapture(null)}
        />
      )}
    </div>
  );
};

export default BayModePage;
